import { BitArray } from "./bits";
import { twoHashes, type Key } from "./hashing";

export interface BloomFilterOptions {
  /** Number of bits in the filter (m). */
  bits: number;
  /** Number of hash probes per item (k). */
  hashes: number;
  /** Seed for the underlying hash, so independent filters can differ. */
  seed?: number;
}

/**
 * A Bloom filter: a space-efficient set that answers "definitely not present"
 * or "probably present". It never returns a false negative, but with some
 * tunable probability it returns a false positive.
 *
 * Probes are generated with the Kirsch–Mitzenmacher double-hashing scheme
 * g_i(x) = h1(x) + i*h2(x), so we pay for only two hash evaluations per item
 * regardless of k.
 */
export class BloomFilter {
  readonly bits: BitArray;
  readonly numHashes: number;
  readonly seed: number;
  /** How many times add() has been called (includes duplicate keys). */
  addedCount = 0;

  constructor(options: BloomFilterOptions) {
    const { bits, hashes, seed = 0 } = options;
    if (bits <= 0) throw new RangeError("bits must be positive");
    if (hashes <= 0) throw new RangeError("hashes must be positive");
    this.bits = new BitArray(bits);
    this.numHashes = hashes;
    this.seed = seed;
  }

  /** Bits in the filter (m). */
  get size(): number {
    return this.bits.size;
  }

  /**
   * Build a filter sized for `expectedItems` distinct items at a target false
   * positive rate, using the standard optimal formulas:
   *   m = -n·ln(p) / (ln 2)^2 ,  k = (m/n)·ln 2
   */
  static optimal(expectedItems: number, falsePositiveRate: number, seed = 0): BloomFilter {
    if (expectedItems <= 0) throw new RangeError("expectedItems must be positive");
    if (falsePositiveRate <= 0 || falsePositiveRate >= 1)
      throw new RangeError("falsePositiveRate must be in (0, 1)");
    const m = Math.max(1, Math.ceil((-expectedItems * Math.log(falsePositiveRate)) / LN2_SQUARED));
    const k = Math.max(1, Math.round((m / expectedItems) * Math.LN2));
    return new BloomFilter({ bits: m, hashes: k, seed });
  }

  private *probes(key: Key): Generator<number> {
    const m = this.bits.size;
    const [h1, h2] = twoHashes(key, this.seed);
    for (let i = 0; i < this.numHashes; i++) {
      // h1 + i*h2 stays < 2^53 for realistic k, so plain float math is exact.
      yield (h1 + i * h2) % m;
    }
  }

  add(key: Key): void {
    for (const idx of this.probes(key)) this.bits.set(idx);
    this.addedCount++;
  }

  /** True if the key is *probably* present; false means *definitely* absent. */
  has(key: Key): boolean {
    for (const idx of this.probes(key)) {
      if (!this.bits.get(idx)) return false;
    }
    return true;
  }

  /** Which bit indices a key touches — used to visualise a lookup. */
  probeIndices(key: Key): number[] {
    return [...this.probes(key)];
  }

  /** Fraction of bits currently set. */
  fillRatio(): number {
    return this.bits.popcount() / this.bits.size;
  }

  /**
   * False-positive probability implied by the *current* fill ratio.
   * If a fraction f of bits are set, a non-member passes all k probes with
   * probability ≈ f^k.
   */
  currentFalsePositiveRate(): number {
    return Math.pow(this.fillRatio(), this.numHashes);
  }

  /**
   * Theoretical false-positive probability after inserting n distinct items:
   *   (1 - e^{-k·n/m})^k
   */
  expectedFalsePositiveRate(n: number): number {
    const m = this.bits.size;
    return Math.pow(1 - Math.exp((-this.numHashes * n) / m), this.numHashes);
  }
}

const LN2_SQUARED = Math.LN2 * Math.LN2;
